// Maps between the backend FundCustomGroupDTO shape and the frontend
// shape the selector/modal/replicate components expect:
//   { id, name, fundNumbers[], linked, accessibleCount, totalCount, memberAccess }
//
// The backend's member DTOs carry only { fundId, accessible }. The
// frontend keys everything by fundNumber elsewhere, so we use a
// fundId<->fundNumber lookup built from the existing portfolios prop
// (which is fed from /api/funds/offers and carries both id and
// fundNumber on each FundDTO).

export function buildFundIdLookup(portfolios) {
    const byId = new Map();           // id (number) -> fundNumber (string)
    const byFundNumber = new Map();   // fundNumber -> id
    for (const p of portfolios || []) {
        if (p && p.id != null && p.fundNumber != null) {
            byId.set(p.id, p.fundNumber);
            byFundNumber.set(p.fundNumber, p.id);
        }
    }
    return { byId, byFundNumber };
}

export function toFrontendGroup(dto, lookup) {
    const members = (dto && dto.funds) || [];
    const resolved = members
        .map(m => ({ fundNumber: lookup.byId.get(m.fundId), accessible: m.accessible }))
        .filter(m => Boolean(m.fundNumber));
    return {
        id: dto.id,
        name: dto.customGroupName,
        fundNumbers: resolved.map(m => m.fundNumber),
        linked: dto.editMode === 'GROUP',
        accessibleCount: dto.accessibleCount,
        totalCount: dto.totalCount,
        memberAccess: Object.fromEntries(resolved.map(m => [m.fundNumber, m.accessible])),
    };
}

export const editModeOf = (linked) => (linked ? 'GROUP' : 'INDIVIDUAL');

/**
 * Diff old vs new frontend-shape groups. Returns:
 *   {
 *     creates:        [{ id, name, linked, fundNumbers }],       // temp string ids
 *     deletes:        [id],                                       // numeric ids
 *     renames:        [{ id, name }],
 *     modeChanges:    [{ id, linked }],
 *     memberAdds:     [{ id, fundNumber }],
 *     memberRemoves:  [{ id, fundNumber }],
 *   }
 */
export function diffGroups(prev, next) {
    const out = { creates: [], deletes: [], renames: [], modeChanges: [], memberAdds: [], memberRemoves: [] };
    const prevById = new Map((prev || []).map(g => [g.id, g]));
    const nextIds = new Set((next || []).map(g => g.id));

    for (const g of next || []) {
        if (typeof g.id === 'string') {
            // temp id => create
            out.creates.push({
                id: g.id,
                name: g.name,
                linked: g.linked !== false,
                fundNumbers: [...(g.fundNumbers || [])],
            });
            continue;
        }
        const old = prevById.get(g.id);
        if (!old) continue;          // shouldn't happen (numeric id not in prev)
        if (old.name !== g.name) out.renames.push({ id: g.id, name: g.name });
        // modeChange requires an explicit value in `next` — a missing linked
        // field means "no change" (e.g. a caller that doesn't manage the
        // Linked/Individual axis). Treating undefined as Linked would
        // silently flip Individual groups on every save.
        if (g.linked !== undefined) {
            const oldLinked = old.linked !== false;
            const newLinked = g.linked !== false;
            if (oldLinked !== newLinked) out.modeChanges.push({ id: g.id, linked: newLinked });
        }

        const oldSet = new Set(old.fundNumbers || []);
        const newSet = new Set(g.fundNumbers || []);
        for (const fn of newSet) if (!oldSet.has(fn)) out.memberAdds.push({ id: g.id, fundNumber: fn });
        for (const fn of oldSet) if (!newSet.has(fn)) out.memberRemoves.push({ id: g.id, fundNumber: fn });
    }

    for (const g of prev || []) {
        if (typeof g.id === 'number' && !nextIds.has(g.id)) out.deletes.push(g.id);
    }
    return out;
}

/**
 * Run the wire ops produced by diffGroups() against the customGroupsApi.
 * Sequential within each phase so member-adds against just-created groups
 * see the freshly-returned numeric ids. Caller passes `idFor` to translate
 * fundNumber → fundId before hitting the addFund/removeFund endpoints.
 *
 * Returns { createdIds } — a Map of temp string id → created numeric id, so
 * the caller can find the just-created groups in a refetched list (e.g. to
 * run the Linked-mode content sync over a brand-new group's members).
 *
 * Pure relative to React; mockable via a stub `api`.
 */
export async function applySaveGroupsOps(ops, api, idFor) {
    const createdIds = new Map();
    // Creates first; their member adds resolve against the freshly returned numeric id
    for (const g of ops.creates) {
        const dto = await api.create({ customGroupName: g.name, editMode: g.linked === false ? 'INDIVIDUAL' : 'GROUP' });
        createdIds.set(g.id, dto.id);
        for (const fn of g.fundNumbers) {
            const fid = idFor(fn);
            if (fid != null) await api.addFund(dto.id, fid);
        }
    }
    for (const id of ops.deletes) await api.deleteGroup(id);
    // Coalesce rename + mode-change for the same group into a single PUT
    const updates = new Map();
    for (const r of ops.renames) updates.set(r.id, { ...(updates.get(r.id) || {}), customGroupName: r.name });
    for (const m of ops.modeChanges) updates.set(m.id, { ...(updates.get(m.id) || {}), editMode: m.linked === false ? 'INDIVIDUAL' : 'GROUP' });
    for (const [id, body] of updates) await api.update(id, body);
    for (const a of ops.memberAdds) {
        const fid = idFor(a.fundNumber);
        if (fid != null) await api.addFund(a.id, fid);
    }
    for (const r of ops.memberRemoves) {
        const fid = idFor(r.fundNumber);
        if (fid != null) await api.removeFund(r.id, fid);
    }
    return { createdIds };
}
